import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import '../../css/index.css';
import '../../css/Pages.css';
import Collapsible from './Components/KlantCollapsible.js';
import useKlanten from './UseKlanten';

const KlantenPagina = () => {
  const { t } = useTranslation();
  const { klanten, loading, error } = useKlanten();
  const [displayKlanten, setDisplayKlanten] = useState(null);
  const [selectedKlanten, setSelectedKlanten] = useState(null);
  const [actieveKlanten, setActieveKlanten] = useState(true);
  const [searchRelatieNr, setSearchRelatieNr] = useState("");
  const [searchBedrijfsnaam, setSearchBedrijfsnaam] = useState("");
  const [sortKey, setSortKey] = useState("naam");

  // Filter and process klanten data
  const processedDisplayKlanten = useMemo(() => {
    if (!klanten) return null;

    let displayklanten = [];
    const currdate = new Date().getTime();

    klanten.forEach((klant) => {
      let hasActiveSubscription = false;

      if (klant.abonnementen && klant.abonnementen.length > 0) {
        for (let a = 0; a < klant.abonnementen.length; a++) {
          const abodate = new Date(klant.abonnementen[a].einddatum).getTime();
          if (klant.abonnementen[a].einddatum == null || abodate > currdate) {
            hasActiveSubscription = true;
            break;
          }
        }
      }

      const includeKlant = actieveKlanten ? hasActiveSubscription : !hasActiveSubscription;
      if (includeKlant) {
        displayklanten.push(klant);
      }
    });

    return displayklanten;
  }, [klanten, actieveKlanten]);

  const sortKlanten = useCallback((items, key) => {
    if (!items) return items;
    const sorted = [...items].sort((a, b) => {
      if (key === "relaNr") {
        if (a.relaNr < b.relaNr) return -1;
        if (a.relaNr > b.relaNr) return 1;
        return 0;
      }
      if (a.naam < b.naam) return -1;
      if (a.naam > b.naam) return 1;
      return 0;
    });
    return sorted;
  }, []);

  // Sort functions
  const orderByNaam = useCallback(() => {
    if (!displayKlanten) return;
    setSortKey("naam");
    setDisplayKlanten(sortKlanten(displayKlanten, "naam"));
    setSelectedKlanten(null);
  }, [displayKlanten, sortKlanten]);

  const orderByRelanr = useCallback(() => {
    if (!displayKlanten) return;
    setSortKey("relaNr");
    setDisplayKlanten(sortKlanten(displayKlanten, "relaNr"));
    setSelectedKlanten(null);
  }, [displayKlanten, sortKlanten]);

  // Update displayKlanten when processedDisplayKlanten changes
  useEffect(() => {
    if (processedDisplayKlanten) {
      const sorted = sortKlanten(processedDisplayKlanten, sortKey);
      setDisplayKlanten(sorted);
    }
  }, [processedDisplayKlanten, sortKey, sortKlanten]);

  // Handle search filters
  const filteredKlanten = useMemo(() => {
    if (!displayKlanten) return null;

    const hasSearch = searchRelatieNr.trim() || searchBedrijfsnaam.trim();
    let filtered = [...displayKlanten];

    if (searchRelatieNr.trim()) {
      filtered = filtered.filter((klant) =>
        klant.relaNr.toString().includes(searchRelatieNr)
      );
    }

    if (searchBedrijfsnaam.trim()) {
      filtered = filtered.filter((klant) =>
        klant.naam.toString().toLowerCase().includes(searchBedrijfsnaam.toLowerCase())
      );
    }

    return hasSearch ? filtered : displayKlanten;
  }, [displayKlanten, searchRelatieNr, searchBedrijfsnaam]);

  const handleSearchChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name === "txtRelatieNr") {
      setSearchRelatieNr(value);
    } else if (name === "txtBedrijfsnaam") {
      setSearchBedrijfsnaam(value);
    }
    setSelectedKlanten(null);
  }, []);

  const handleToggleActieveKlanten = useCallback(() => {
    setActieveKlanten((prev) => !prev);
    setSelectedKlanten(null);
  }, []);

  const displayedKlanten = selectedKlanten || filteredKlanten || displayKlanten;

  // Loading state
  if (loading) {
    return (
      <div className="loading-container">
          <div className="loading-text">{t('customers.loading')}</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="error-alert">
          <h3>{t('common.error')}</h3>
          <p>{t('customers.noCustomersFound')}</p>
      </div>
    );
  }

  // Empty state
  if (!klanten || !displayKlanten) {
    return (
      <div className="loading-container">
          <div className="loading-text">{t('common.noDataAvailable')}</div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      {/* Header */}
      <div className="page-header">
          <h2>{t('customers.title')}</h2>
      </div>

      <div className="page-content">
        {/* Filter Bar */}
        <div className="filter-bar">
            <h3>{t('customers.searchOptions')}</h3>
          <div className="filter-grid">
            <div className="filter-group">
                <label className="filter-label">{t('customers.relationNumber')}</label>
              <input
                className="filter-input"
                type="text"
                name="txtRelatieNr"
                  placeholder={t('customers.searchByRelationNumber')}
                value={searchRelatieNr}
                onChange={handleSearchChange}
              />
            </div>
            <div className="filter-group">
                <label className="filter-label">{t('customers.companyName')}</label>
              <input
                className="filter-input"
                type="text"
                name="txtBedrijfsnaam"
                  placeholder={t('customers.searchByCompanyName')}
                value={searchBedrijfsnaam}
                onChange={handleSearchChange}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">&nbsp;</label>
              <div className="filter-checkbox" style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  type="checkbox"
                  id="activeKlantenCheckbox"
                  checked={actieveKlanten}
                  onChange={handleToggleActieveKlanten}
                  style={{ width: "1rem", height: "1rem", margin: 0 }}
                />
                <label htmlFor="activeKlantenCheckbox" style={{ margin: 0, fontWeight: "500", fontSize: "0.875rem", cursor: "pointer" }}>
                    {t('customers.activeCustomers')}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="table-scroll" style={{ borderRadius: "0.5rem", border: "2px solid #d1d5db", boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", overflow: "hidden" }}>
          <table className="table" name="klantenTable" style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "100%" }}>
            <colgroup>
              <col style={{ width: "40%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "20%" }} />
            </colgroup>
            <thead>
              <tr>
                <th onClick={orderByNaam} style={{ cursor: "pointer", userSelect: "none", borderRight: "1px solid #d1d5db" }}>
                  {t('customers.name')}
                </th>
                <th onClick={orderByRelanr} style={{ cursor: "pointer", userSelect: "none", borderRight: "1px solid #d1d5db" }}>
                  {t('customers.relationNumber')}
                </th>
                <th colSpan="2"></th>
              </tr>
            </thead>
            <tbody style={{ borderCollapse: "collapse", display: "table-row-group", width: "100%", backgroundColor: "white", border: "2px solid #d1d5db" }}>
              {displayedKlanten && displayedKlanten.length > 0 ? (
                displayedKlanten.map((klant, index) => (
                  <Collapsible key={`${klant.id || index}`} klant={klant} index={index} />
                ))
              ) : (
                <tr className="empty-row">
                    <td colSpan="3">{t('customers.noCustomers')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default KlantenPagina;